/**
 * Open-redirect guard (frontend-architecture R3 / security: open-redirect).
 *
 * Returns a SAFE, same-origin, relative path, or '/' if `raw` is anything
 * we would not navigate to. Used in BOTH directions:
 *   - when WRITING `?next=` (RequireAuth captures location.pathname+search), and
 *   - when READING `appState.returnTo` back in onRedirectCallback,
 * before either value is handed to React Router's navigate(). Defence in
 * depth: `appState.returnTo` round-trips through the /authorize redirect and
 * is therefore attacker-influenceable.
 *
 * Accept rule (must satisfy ALL, on both the raw and one-decode forms):
 *   1. non-empty string
 *   2. starts with exactly one '/'   (rejects '', 'foo', 'http:...', 'javascript:...')
 *   3. second char is NOT '/' and NOT '\\'  (rejects '//host' protocol-relative, '/\\host')
 *   4. contains no '\\' anywhere     (backslash path tricks; browsers fold '\\' to '/')
 *   5. contains no control chars (\u0000-\u001F, \u007F)  (CR/LF/NUL smuggling)
 *
 * On success we return the ORIGINAL `raw` verbatim (already a valid relative
 * path); the decode pass is a GATE, not a transform.
 */
export function safeNextPath(raw: string | null | undefined): string {
  const FALLBACK = '/';
  if (raw == null || raw === '') return FALLBACK;

  let decoded: string;
  try {
    decoded = decodeURIComponent(raw);
  } catch {
    // Malformed percent-encoding is hostile input.
    return FALLBACK;
  }

  for (const candidate of [raw, decoded]) {
    if (!isSafeRelative(candidate)) return FALLBACK;
  }
  return raw;
}

function isSafeRelative(s: string): boolean {
  if (s.length === 0) return false;
  if (s[0] !== '/') return false; // must be path-absolute
  if (s[1] === '/' || s[1] === '\\') return false; // not '//' or '/\\'
  if (s.includes('\\')) return false; // no backslash anywhere
  // eslint-disable-next-line no-control-regex -- intentional control-char scan
  if (/[\u0000-\u001F\u007F]/.test(s)) return false; // no control chars (CRLF/NUL)
  return true;
}
